import React from 'react';
import LazyImage from '@/components/LazyImage';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Check, User, Loader2, Video as VideoIcon, Play, Pause, Download, Share2 } from 'lucide-react';
import { theme } from '@/lib/theme';
import { supabase } from '@/integrations/supabase/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/free-solid-svg-icons';
import { useToast } from '@/hooks/use-toast';
import VideoCard from '@/components/chitra/VideoCard';
import GeneratingLoader from '@/components/GeneratingLoader';
import { getOptimizedCloudinaryUrl } from '@/utils/cloudinary';

export type GeneratedImage = {
  id?: string;
  url: string;
  uploaded: boolean;
  uploading: boolean;
  prompt?: string;
  style?: string;
  cloudinaryUrl?: string;
  publicId?: string;
  createdAt?: string | Date;
  aspect_ratio?: string;
  cloudinary_public_id?: string;
  image_url?: string;
  likes_count?: number;
  user_id?: string;
  creator_name?: string;
  creator_avatar?: string;
  attached_media?: string[] | null;
  // Video fields
  video_id?: string;
  video_url?: string;
  thumbnail_url?: string;
  status?: 'pending' | 'processing' | 'completed' | 'failed';
  isVideo?: boolean;
  isGeneratingPlaceholder?: boolean;
  processingToolName?: string;
};

interface ImagesGridProps {
  images: GeneratedImage[];
  visibleCount: number;
  isDarkMode: boolean;
  isHovered: string | null;
  setIsHovered: (imageId: string | null) => void;
  onOpenFullscreen: (image: GeneratedImage) => void;
  user?: any;
  onShowMore?: () => void;
  isLoading?: boolean;
  onOpenVideoFullscreen?: (video: GeneratedImage) => void;
}

export const ImagesGrid: React.FC<ImagesGridProps> = ({ images, visibleCount, isDarkMode, isHovered, setIsHovered, onOpenFullscreen, user, onShowMore, isLoading, onOpenVideoFullscreen }) => {
  const [columnCount, setColumnCount] = React.useState(1);
  const [columns, setColumns] = React.useState<GeneratedImage[][]>([]);
  const lastIndexAddedRef = React.useRef(0);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const prevFirstIdRef = React.useRef<string | undefined>(undefined);
  const [likedIds, setLikedIds] = React.useState<Set<string>>(new Set());
  const [likeCounts, setLikeCounts] = React.useState<Record<string, number>>({});
  const [poppingId, setPoppingId] = React.useState<string | null>(null);
  const { toast } = useToast();
  const [eagerRowsPerColumn, setEagerRowsPerColumn] = React.useState(3);

  const getAspectClass = (aspect?: string) => {
    const ratio = aspect || "3:4";
    switch (ratio) {
      case "1:1":
        return "aspect-square";
      case "16:9":
        return "aspect-video";
      case "9:16":
        return "aspect-[9/16]";
      case "4:3":
        return "aspect-[4/3]";
      case "3:2":
        return "aspect-[3/2]";
      case "21:9":
        return "aspect-[21/9]";
      case "2:3":
        return "aspect-[2/3]";
      case "3:4":
      default:
        return "aspect-[3/4]";
    }
  };

  const getThumbWidth = React.useCallback(() => {
    if (typeof window === 'undefined') return 480;
    const w = window.innerWidth || 1200;
    const gap = 8;
    const cols = Math.max(1, columnCount);
    return Math.min(900, Math.max(220, Math.ceil((w - gap * (cols - 1)) / cols)));
  }, [columnCount]);

  // Viewport-based eager loading: eagerly load enough items to fill ~1.5 screens.
  // Since masonry items vary in height, we estimate an average tile height from thumb width.
  React.useEffect(() => {
    const updateEagerRows = () => {
      if (typeof window === 'undefined') return;
      const viewportH = window.innerHeight || 800;
      const thumbW = getThumbWidth();
      const estimatedTileH = Math.max(180, Math.round(thumbW * 0.85));
      const rows = Math.ceil((viewportH * 1.5) / estimatedTileH);
      setEagerRowsPerColumn(Math.max(2, Math.min(10, rows)));
    };

    updateEagerRows();
    window.addEventListener('resize', updateEagerRows);
    return () => window.removeEventListener('resize', updateEagerRows);
  }, [getThumbWidth]);

  const VideoRenderer: React.FC<{ src?: string; poster?: string; className?: string; onClick?: (e: React.MouseEvent) => void }> = ({ src, poster, className, onClick }) => {
    const [fallback, setFallback] = React.useState(false);
    if (!src) {
      return (
        <div className="w-full aspect-video rounded-xl flex items-center justify-center bg-gray-500/10">
          <VideoIcon className="w-8 h-8 text-gray-400" />
        </div>
      );
    }
    return fallback ? (
      <iframe
        src={src}
        className={cn("w-full aspect-video rounded-xl", className)}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        onClick={onClick}
      />
    ) : (
      <video
        src={src}
        poster={poster}
        controls
        className={cn("w-full aspect-video rounded-xl object-cover", className)}
        preload="metadata"
        onError={() => setFallback(true)}
        onClick={onClick}
      />
    );
  };

  // Create a unique identifier for each image to fix hover state
  const getImageId = (image: GeneratedImage, colIndex: number, rowIndex: number) => {
    // Use multiple fallbacks to ensure uniqueness
    if (image.id) return image.id;
    if (image.video_id) return `video-${image.video_id}`;
    // Create a stable hash from URL, timestamp, and position
    const urlHash = image.url ? image.url.substring(image.url.length - 30) : 'no-url';
    const timestamp = image.createdAt ? new Date(image.createdAt).getTime() : 0;
    return `img-${urlHash}-${timestamp}-${colIndex}-${rowIndex}`;
  };

  // Detect PNG/transparent images by URL or data URL mime
  const isPngImage = (url: string) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.endsWith('.png') || lower.startsWith('data:image/png');
  };

  // Checkerboard background for transparency preview
  const checkerboardStyle = (dark: boolean): React.CSSProperties => {
    const lightBase = '#ffffff';
    const lightSquare = '#e5e7eb'; // gray-200
    const darkBase = '#1a1a1a';
    const darkSquare = '#2a2a2a'; // darker gray for contrast

    const base = dark ? darkBase : lightBase;
    const square = dark ? darkSquare : lightSquare;

    return {
      backgroundColor: base,
      backgroundImage: `
      linear-gradient(45deg, ${square} 25%, transparent 25%, transparent 75%, ${square} 75%, ${square}),
      linear-gradient(45deg, ${square} 25%, transparent 25%, transparent 75%, ${square} 75%, ${square})
    `,
      backgroundPosition: '0 0, 8px 8px',
      backgroundSize: '16px 16px',
    };
  };

  // Determine responsive column count similar to Tailwind breakpoints
  React.useEffect(() => {
    const updateColumns = () => {
      const w = window.innerWidth;
      let count = 1;
      if (w >= 1024) count = 4; // lg
      else if (w >= 768) count = 3; // md
      else if (w >= 640) count = 2; // sm
      else count = 1;
      setColumnCount(count);
    };
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  // Initialize or reset columns when columnCount changes
  React.useEffect(() => {
    const initCols: GeneratedImage[][] = Array.from({ length: columnCount }, () => []);
    const initialItems = images.slice(0, visibleCount);
    initialItems.forEach((img, idx) => {
      initCols[idx % columnCount].push(img);
    });
    setColumns(initCols);
    lastIndexAddedRef.current = initialItems.length;
    // We intentionally only depend on columnCount to avoid redistributing on every images update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnCount]);

  // Reset columns when the images array is prepended/changed at the start
  React.useEffect(() => {
    const currentFirst = images[0]?.id || images[0]?.url;
    if (currentFirst !== prevFirstIdRef.current) {
      const initCols: GeneratedImage[][] = Array.from({ length: columnCount }, () => []);
      const initialItems = images.slice(0, visibleCount);
      initialItems.forEach((img, idx) => {
        initCols[idx % Math.max(1, columnCount)].push(img);
      });
      setColumns(initCols);
      lastIndexAddedRef.current = initialItems.length;
      prevFirstIdRef.current = currentFirst;
    }
  }, [images, visibleCount, columnCount]);

  // Append newly visible items without redistributing existing ones
  React.useEffect(() => {
    const nextIndex = lastIndexAddedRef.current;
    const targetCount = Math.min(visibleCount, images.length);
    if (targetCount <= nextIndex || columnCount <= 0) return;

    setColumns((prev) => {
      const next = prev.map((col) => [...col]);
      let colPtr = 0;
      for (let i = nextIndex; i < targetCount; i++) {
        next[colPtr % Math.max(1, columnCount)].push(images[i]);
        colPtr++;
      }
      return next;
    });
    lastIndexAddedRef.current = targetCount;
  }, [visibleCount, images, columnCount]);

  // Seed like counts from incoming images
  React.useEffect(() => {
    const next: Record<string, number> = {};
    images.forEach((img) => {
      if (img.id) next[img.id] = img.likes_count || 0;
    });
    setLikeCounts((prev) => ({ ...next, ...prev }));
  }, [images]);

  // Fetch liked image ids for current user
  React.useEffect(() => {
    const fetchLiked = async () => {
      if (!user?.id) {
        setLikedIds(new Set());
        return;
      }
      const imageIds = images.map((i) => i.id).filter((id) => id && id !== 'generating-placeholder' && !id.startsWith('temp-')) as string[];
      if (imageIds.length === 0) return;
      const { data, error } = await supabase
        .from('image_likes')
        .select('image_id')
        .eq('user_id', user.id)
        .in('image_id', imageIds);
      if (error) {
        console.error('Fetch liked ids error', error);
        return;
      }
      setLikedIds(new Set((data || []).map((r: any) => r.image_id)));
    };
    fetchLiked();
  }, [user?.id, images]);

  const toggleLike = async (image: GeneratedImage, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!image.id) return;
    if (!user?.id) {
      toast({ title: 'Sign in required', description: 'Please sign in to like images.' });
      try { window.dispatchEvent(new CustomEvent('openAuthModal')); } catch { }
      return;
    }
    if (user?.id && image.user_id && user.id === image.user_id) {
      return;
    }
    const isLiked = likedIds.has(image.id);
    // optimistic update
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (isLiked) next.delete(image.id!);
      else next.add(image.id!);
      return next;
    });
    setLikeCounts((prev) => ({
      ...prev,
      [image.id!]: Math.max(0, (prev[image.id!] ?? image.likes_count ?? 0) + (isLiked ? -1 : 1)),
    }));
    setPoppingId(image.id);
    window.setTimeout(() => setPoppingId((id) => (id === image.id ? null : id)), 200);

    if (isLiked) {
      const { error } = await supabase
        .from('image_likes')
        .delete()
        .eq('image_id', image.id)
        .eq('user_id', user.id);
      if (error) console.error('Unlike error', error);
    } else {
      const { error } = await supabase
        .from('image_likes')
        .insert({ image_id: image.id, user_id: user.id });
      if (error) console.error('Like error', error);
    }
  };

  // Intersection Observer for infinite loading
  React.useEffect(() => {
    if (!onShowMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          onShowMore();
        }
      },
      { root: null, rootMargin: '400px 0px', threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [onShowMore, visibleCount]);

  // Get display name for a specific image's creator
  const getDisplayName = (image: GeneratedImage) => {
    return image.creator_name || '';
  };

  // Get avatar URL for a specific image's creator
  const getAvatarUrl = (image: GeneratedImage) => {
    return image.creator_avatar;
  };

  return (
    <>
      {/* Stable Masonry: fixed number of columns with stacked items to avoid redistribution */}
      <div className="w-full pb-16 mb-10 relative">
        {isLoading && (
          <div className="absolute inset-0 z-10 rounded-2xl overflow-hidden pointer-events-none">
            <div
              className="absolute inset-0 animate-pulse"
            />
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.04) 30%, rgba(0,0,0,0.07) 50%, rgba(0,0,0,0.04) 70%, transparent 100%)`,
                backgroundSize: '200% 100%',
                mixBlendMode: isDarkMode ? 'screen' : 'multiply',
              }}
            />
          </div>
        )}
        <div className="flex gap-1">
          {columns.map((col, cIdx) => (
            <div key={cIdx} className="flex-1 min-w-0 flex flex-col gap-2 sm:gap-1">
              {col.map((image, index) => {
                const imageId = getImageId(image, cIdx, index);

                return (
                  <div
                    key={imageId}
                    className={`relative group cursor-pointer overflow-hidden rounded-none transition-all duration-300 border-0 p-0`}
                    onMouseEnter={() => setIsHovered(imageId)}
                    onMouseLeave={() => setIsHovered(null)}
                    onClick={() => { if (!image.isVideo && !image.isGeneratingPlaceholder) onOpenFullscreen(image); }}
                  >
                    {image.isGeneratingPlaceholder ? (
                      <div className={cn(
                        "w-full rounded-xl flex flex-col items-center justify-center border overflow-hidden",
                        getAspectClass(image.aspect_ratio),
                        isDarkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-gray-200"
                      )}>
                        <GeneratingLoader isDarkMode={isDarkMode} />
                        {image.processingToolName && (
                          <div className="mt-[-20px] text-center px-2 z-10">
                            <p className={cn("text-xs font-medium", isDarkMode ? "text-white/80" : "text-gray-700")}>
                              {image.processingToolName}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : image.isVideo ? (
                      // Video rendering
                      image.status === 'pending' || image.status === 'processing' ? (
                        <div className="w-full aspect-video rounded-xl flex flex-col items-center justify-center bg-gradient-to-br from-purple-500/20 to-blue-500/20">
                          <Loader2 className="w-8 h-8 animate-spin text-purple-500 mb-2" />
                          <span className="text-sm capitalize">{image.status}...</span>
                        </div>
                      ) : image.status === 'failed' ? (
                        <div className="w-full aspect-video rounded-xl flex items-center justify-center bg-red-500/10">
                          <div className="text-center">
                            <VideoIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                            <span className="text-sm text-red-500">Failed to generate</span>
                          </div>
                        </div>
                      ) : image.video_url ? (
                        <VideoCard
                          src={image.video_url}
                          poster={image.thumbnail_url}
                          isDarkMode={isDarkMode}
                          onFullscreen={() => onOpenVideoFullscreen && onOpenVideoFullscreen(image)}
                        />
                      ) : (
                        <div className="w-full aspect-video rounded-xl flex items-center justify-center bg-gray-500/10">
                          <VideoIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )
                    ) : (
                      // Image rendering
                      <LazyImage
                        src={getOptimizedCloudinaryUrl(image.url, {
                          width: getThumbWidth(),
                          quality: 'auto:good',
                          format: 'auto',
                          dpr: 'auto',
                          crop: 'limit',
                        })}
                        alt={image.prompt || `Generated image`}
                        className="w-full h-auto object-cover rounded-none transition-transform duration-500 ease-in-out h-[max-content]"
                        style={{
                          // Checkerboard pattern for transparent images - only show on hover
                          backgroundImage: isHovered === imageId
                            ? (isDarkMode
                              ? `linear-gradient(45deg, #2a2a2a 25%, transparent 25%), 
                                   linear-gradient(-45deg, #2a2a2a 25%, transparent 25%), 
                                   linear-gradient(45deg, transparent 75%, #2a2a2a 75%), 
                                   linear-gradient(-45deg, transparent 75%, #2a2a2a 75%)`
                              : `linear-gradient(45deg, #e0e0e0 25%, transparent 25%), 
                                   linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), 
                                   linear-gradient(45deg, transparent 75%, #e0e0e0 75%), 
                                   linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)`)
                            : 'none',
                          backgroundSize: '20px 20px',
                          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                          backgroundColor: isHovered === imageId
                            ? (isDarkMode ? '#1a1a1a' : '#f5f5f5')
                            : 'transparent',
                        }}
                        isDarkMode={isDarkMode}
                        showPlaceholder={true}
                        placeholderEffect="shimmer"
                        rootMargin="600px"
                        forceLoad={index < eagerRowsPerColumn}
                        prefetchUrls={(() => {
                          const baseIndex = cIdx + index * columnCount;
                          const next: string[] = [];
                          for (let j = 1; j <= 4; j++) {
                            const candidate = images[baseIndex + j];
                            if (candidate?.url) {
                              next.push(
                                getOptimizedCloudinaryUrl(candidate.url, {
                                  width: getThumbWidth(),
                                  quality: 'auto:good',
                                  format: 'auto',
                                  dpr: 'auto',
                                  crop: 'limit',
                                })
                              );
                            }
                          }
                          return next;
                        })()}
                      />
                    )}
                    {/* Always-visible details */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div
                        className={cn(
                          'absolute inset-0',
                          isDarkMode
                            ? 'bg-transparent'
                            : 'bg-transparent'
                        )}
                      />
                      <div className="absolute inset-x-0 bottom-0 p-3 pb-3 flex flex-col gap-2 select-none">
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center pl-2 instrument-serif-regular">
                            {image.user_id && (
                              <>
                                <span className={cn('text-sm font-medium', isDarkMode ? 'text-white' : 'text-slate-900')}>
                                  {getDisplayName(image)}
                                </span>
                              </>
                            )}
                          </div>
                          {!image.isVideo && !image.isGeneratingPlaceholder && (
                            <button
                              className={cn(
                                'flex items-center gap-2 px-2 py-1 rounded-full pointer-events-auto',
                                isDarkMode ? 'hover:bg-black/25' : 'hover:bg-white/25',
                              )}
                              onClick={(e) => toggleLike(image, e)}
                              aria-label="Like image"
                              title="Like"
                              style={{
                                color: likedIds.has(image.id || '')
                                  ? (isDarkMode ? '#ffffffff' : '#de0736ff')
                                  : (isDarkMode ? '#e5e7eb' : '#111827'),
                              }}
                            >
                              <FontAwesomeIcon
                                icon={likedIds.has(image.id || '') ? faHeartSolid : faHeartRegular}
                                className="h-4 w-4 transition-transform duration-200"
                                style={{ transform: poppingId === (image.id || '') ? 'scale(1.15)' : 'scale(1)' }}
                              />
                              <span className={cn('text-xs font-semibold', isDarkMode ? 'text-white' : 'text-slate-900')}>
                                {likeCounts[image.id || ''] ?? image.likes_count ?? 0}
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {/* Infinite scroll sentinel */}
        {images.length > visibleCount && (
          <div ref={sentinelRef} className="w-full h-8 mt-6" />
        )}
      </div>
    </>
  );
};

export default ImagesGrid;



